import { Skeleton, Stack, TextField } from '@mui/material';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  getGeographicBoundary,
  getGeographicBoundaryHierarchies,
} from '../api';
import { geographicBoundaryTypeCountryId } from '../constants';
import { useGeographicBoundary } from '../hooks/api/geographic-boundary';
import { useAuth } from '../hooks/useAuth';
import { IGeographicBoundaryResult } from '../utils/types';
import { AutocompleteDialog } from './AutocompleteDialog';

export function NestedGeographicBoundarySelect(props: {
  value?: number;
  onChange?: (value: number) => void;
  formDirty?: boolean;
}) {
  const defaultCountry = {
    id: 18884,
    name: 'Türkiye',
    type: {
      id: geographicBoundaryTypeCountryId,
      code: 'COUNTRY',
      name: 'Country',
      description: 'Country',
      status: true,
    },
    code: 'TR',
    description: 'Türkiye',
  };
  const [selection, setSelection] = useState<IGeographicBoundaryResult[]>([
    defaultCountry,
  ]);
  const [showNewSelection, setShowNewSelection] = useState(true);
  const { token } = useAuth();

  useEffect(() => {
    if (props.value) {
      // skip if value is already selected
      if (selection[selection.length - 1]?.id === props.value) return;
      getGeographicBoundary(props.value, token).then((leaf) => {
        getGeographicBoundaryHierarchies({
          id: props.value as number,
          authToken: token,
        }).then((data) => {
          if (!data || data.length === 0) return;
          const newSelection = [...data].splice(0, data.length - 1);
          newSelection.reverse();
          setSelection([...newSelection, leaf]);
        });
      });
      setShowNewSelection(false);
    } else {
      setShowNewSelection(true);
      setSelection([defaultCountry]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.value]);

  return (
    <Stack direction="column" spacing={2}>
      {selection.map((s, i) => (
        <GeographicBoundarySelect
          key={s.id}
          onChange={(value) => {
            const newSelection = selection.splice(0, i);
            if (value) {
              newSelection.push(value);
            }
            setShowNewSelection(true);
            setSelection(newSelection);
          }}
          value={selection[i]}
          parentId={i > 0 ? selection[i - 1].id : undefined}
          typeId={i === 0 ? geographicBoundaryTypeCountryId : undefined}
        />
      ))}
      {showNewSelection && (
        <GeographicBoundarySelect
          value={null}
          onChange={(value) => {
            if (value) {
              setSelection([...selection, value]);
            }
          }}
          typeId={
            selection.length === 0 ? geographicBoundaryTypeCountryId : undefined
          }
          parentId={
            selection.length === 0
              ? undefined
              : selection[selection.length - 1].id
          }
          onNoOptions={() => {
            console.log('onNoOptions', selection[selection.length - 1]);
            props.onChange?.(selection[selection.length - 1]?.id);
            setShowNewSelection(false);
          }}
          openInitially={selection.length > 1}
          touched={props.formDirty}
        />
      )}
    </Stack>
  );
}
function GeographicBoundarySelect(props: {
  parentId?: number;
  typeId?: number;
  onChange: (value: IGeographicBoundaryResult | null) => void;
  value?: IGeographicBoundaryResult | null;
  onNoOptions?: () => void;
  openInitially?: boolean;
  touched?: boolean;
}) {
  const { t } = useTranslation();
  const { data, isLoading, error } = useGeographicBoundary({
    parentId: props.parentId,
    typeId: props.typeId,
  });

  // add label keys for translation extractor
  t('geographicBoundary.label.COUNTRY');
  t('geographicBoundary.label.CITY');
  t('geographicBoundary.label.DISTRICT');
  t('geographicBoundary.label.NEIGHBORHOOD');
  t('geographicBoundary.label.STREET');
  t('geographicBoundary.label.STATE');
  t('geographicBoundary.label.POSTAL_CODE');
  t('geographicBoundary.label.PREFECTURE');
  t('geographicBoundary.label.PROVINCE');
  t('geographicBoundary.label.CANTON');
  t('geographicBoundary.label.TERRITORY');

  useEffect(() => {
    if (isLoading) return;
    if (error || data?.length === 0) {
      props.onNoOptions?.();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, isLoading]);

  if (isLoading) return <Skeleton height={66} variant="rounded" />;
  return (
    <AutocompleteDialog
      title={
        data?.[0]?.type.code
          ? t('geographicBoundary.label.' + data?.[0]?.type.code)
          : t('common.select')
      }
      renderTrigger={(tprops) => (
        <TextField
          value={props.value?.name || ''}
          label={
            data?.[0]?.type.code
              ? t('geographicBoundary.label.' + data?.[0]?.type.code)
              : t('common.select')
          }
          {...tprops}
          error={!props.value && props.touched}
          helperText={!props.value && props.touched && t('common.required')}
          required
        />
      )}
      options={data ?? []}
      loading={isLoading}
      renderInput={(params) => (
        <TextField
          {...params}
          label={
            data?.[0]?.type.code
              ? t('geographicBoundary.label.' + data?.[0]?.type.code)
              : t('common.select')
          }
        />
      )}
      value={props.value}
      onChange={(_, value) => {
        props.onChange((value as IGeographicBoundaryResult) ?? null);
      }}
      getOptionLabel={(option) => (option as IGeographicBoundaryResult).name}
      isOptionEqualToValue={(option, value) => option.id === value.id}
      openInitially={props.openInitially}
    />
  );
}
